import { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { useSnackbar } from '../../hooks/useSnackbar';

export default function FinancePromptDefaultsDialog({
  open,
  pages,
  currentPageIndex: initialPageIndex,
  grossValue,
  paymentId,
  onFinish,
  suppressForToday,
  saveMutation,
  appointmentId,
  items,
  details,
}) {
  const { showError } = useSnackbar();
  const [currentPageIndex, setCurrentPageIndex] = useState(initialPageIndex || 0);

  const currentPage = useMemo(() => {
    return pages?.[currentPageIndex] || null;
  }, [pages, currentPageIndex]);

  const handleYes = async () => {
    if (!currentPage) return;

    try {
      // Montar payload com o flag correspondente à página atual
      const payload = {
        grossValue,
        paymentId: paymentId || null,
        items,
        promoteServicePrice: currentPage.type === 'servicePrice',
        promotePlacePaymentPrice: currentPage.type === 'placePrice',
        promoteProfessionalPrice: currentPage.type === 'professionalPrice',
        linkPaymentToSchedule: currentPage.type === 'paymentLink',
      };

      const result = await saveMutation.mutateAsync({
        id: appointmentId,
        payload,
      });

      // Avançar para próxima página
      const nextPageIndex = currentPageIndex + 1;
      if (nextPageIndex < pages.length) {
        setCurrentPageIndex(nextPageIndex);
      } else {
        onFinish();
      }
    } catch (err) {
      showError(err.message);
    }
  };

  const handleNo = () => {
    if (!currentPage) return;

    suppressForToday(currentPage.type === 'servicePrice' ? 'defaultPrice'
      : currentPage.type === 'placePrice' ? 'placePrice'
      : currentPage.type === 'professionalPrice' ? 'professionalPrice'
      : 'paymentLink'
    );

    // Avançar para próxima página
    const nextPageIndex = currentPageIndex + 1;
    if (nextPageIndex < pages.length) {
      setCurrentPageIndex(nextPageIndex);
    } else {
      onFinish();
    }
  };

  if (!open || !currentPage) return null;

  const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);
  const pageCount = pages.length;
  const pageNum = currentPageIndex + 1;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      {/* Página 1: Preço do serviço */}
      {currentPage.type === 'servicePrice' && (
        <>
          <DialogTitle>
            Salvar preço padrão do serviço? ({pageNum}/{pageCount})
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2">
              O serviço <strong>{currentPage.serviceName}</strong> ainda não tem um preço padrão.
              Deseja salvar <strong>{fmt(grossValue)}</strong> como preço padrão do serviço?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleNo}>Não</Button>
            <Button variant="contained" onClick={handleYes}>Sim</Button>
          </DialogActions>
        </>
      )}

      {/* Página 2: Preço do local + forma de pagamento */}
      {currentPage.type === 'placePrice' && (
        <>
          <DialogTitle>
            Salvar preço no local? ({pageNum}/{pageCount})
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2">
              O serviço <strong>{currentPage.serviceName}</strong> com{' '}
              <strong>{currentPage.paymentName}</strong> não tem preço definido neste local.
              Deseja salvar <strong>{fmt(grossValue)}</strong> como preço padrão desta combinação no local?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleNo}>Não</Button>
            <Button variant="contained" onClick={handleYes}>Sim</Button>
          </DialogActions>
        </>
      )}

      {/* Página 3: Preço do profissional */}
      {currentPage.type === 'professionalPrice' && (
        <>
          <DialogTitle>
            Salvar preço customizado do profissional? ({pageNum}/{pageCount})
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2">
              <strong>{currentPage.professionalName}</strong> não tem preço customizado para{' '}
              <strong>{currentPage.serviceName}</strong> com <strong>{currentPage.paymentName}</strong> neste local.
              Deseja salvar <strong>{fmt(grossValue)}</strong> como preço customizado deste profissional para essa combinação?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleNo}>Não</Button>
            <Button variant="contained" onClick={handleYes}>Sim</Button>
          </DialogActions>
        </>
      )}

      {/* Página 4: Vincular forma de pagamento */}
      {currentPage.type === 'paymentLink' && (
        <>
          <DialogTitle>
            Vincular forma de pagamento ao serviço neste local? ({pageNum}/{pageCount})
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2">
              A forma de pagamento <strong>{currentPage.paymentName}</strong> ainda não está habilitada para este serviço neste local.
              Deseja habilitá-la como opção de pagamento desta combinação?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleNo}>Não</Button>
            <Button variant="contained" onClick={handleYes}>Sim</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

